import { useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Upload, Loader2, X } from "lucide-react";

/**
 * Drop-in file uploader.
 * Props:
 *   purpose   — string bucket label (e.g. "logo", "audit_pdf", "client_doc")
 *   refType   — optional entity type (e.g. "client", "lead")
 *   refId     — optional entity id
 *   accept    — HTML accept string (defaults to images + PDFs)
 *   onUploaded(file) — callback with the new file row
 *   maxMb     — client-side cap (default 25)
 */
export default function FileUpload({
  purpose = "general",
  refType,
  refId,
  accept = "image/*,application/pdf",
  onUploaded,
  maxMb = 25,
  label = "Upload file",
}) {
  const [dragging, setDragging] = useState(false);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  const doUpload = async (file) => {
    if (!file) return;
    if (file.size > maxMb * 1024 * 1024) {
      toast.error(`File too large. Max ${maxMb} MB.`);
      return;
    }
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const params = { purpose };
      if (refType) params.ref_type = refType;
      if (refId) params.ref_id = refId;
      const { data } = await api.post("/files/upload", form, {
        headers: { "Content-Type": "multipart/form-data" },
        params,
      });
      toast.success(`Uploaded ${data.original_filename}`);
      onUploaded?.(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
      onDragLeave={() => setDragging(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDragging(false);
        const f = e.dataTransfer.files?.[0];
        if (f) doUpload(f);
      }}
      onClick={() => inputRef.current?.click()}
      data-testid="file-upload-dropzone"
      className={`border-2 border-dashed ${dragging ? "border-coral bg-coral/5" : "border-neutral-300 bg-white"} px-6 py-8 text-center cursor-pointer transition hover:border-neutral-900`}
    >
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        className="hidden"
        data-testid="file-upload-input"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) doUpload(f);
          e.target.value = "";
        }}
      />
      {busy ? (
        <div className="flex items-center justify-center gap-2 text-neutral-600">
          <Loader2 className="w-4 h-4 animate-spin" /> Uploading…
        </div>
      ) : (
        <div>
          <Upload className="w-6 h-6 mx-auto mb-2 text-neutral-500" />
          <div className="font-semibold text-neutral-900">{label}</div>
          <div className="text-xs text-neutral-500 mt-1">
            Drop a file or click to browse · Max {maxMb} MB
          </div>
        </div>
      )}
    </div>
  );
}

/** Authenticated blob-backed preview (works around <img> not sending Bearer). */
export function useAuthedFileUrl(fileId) {
  const [url, setUrl] = useState(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const load = async () => {
    if (!fileId) return;
    try {
      const resp = await api.get(`/files/${fileId}/download`, { responseType: "blob" });
      setUrl(URL.createObjectURL(resp.data));
    } catch { /* noop */ }
  };
  return { url, load };
}
